/**
 * Tasks — submit a capability run and observe its lifecycle.
 *
 * Platform admins are treated as internal by the backend, so org-membership
 * checks are bypassed — they can submit against any agent and see every task,
 * including operator-test runs.
 */
import { apiEventStream, apiGet, apiGetWithHeaders, apiPost } from '../client.js';
import type {
  SubmitTaskBody,
  Task,
  TaskLinkage,
  TaskLog,
  TaskOutcome,
  TaskPayload,
  TaskProfile,
  TaskSource,
  TaskStatus,
} from '../types.js';

/** The header the list endpoint carries its unpaged total in. */
const TOTAL_COUNT_HEADER = 'X-Total-Count';

export interface ListTasksFilters {
  organization_id?: string;
  agent_id?: string;
  capability_id?: string;
  status?: TaskStatus[];
  source?: TaskSource[];
  /** ISO-8601 datetime, inclusive lower bound on `created_at`. */
  created_after?: string;
  /** ISO-8601 datetime, inclusive upper bound on `created_at`. */
  created_before?: string;
  /** The result's boolean `success`, or `none` for a task without one. */
  success?: TaskOutcome;
  limit?: number;
  offset?: number;
  /** One first task's linked tasks (Feature 71). */
  parent_task_id?: string;
  /** Every task without a parent: linked tasks left out (Feature 71 D33). */
  top_level?: boolean;
}

/** One page of tasks and the unpaged total under the same filters. */
export interface TaskPage {
  items: Task[];
  total: number;
}

function listQuery(filters: ListTasksFilters): string {
  const qs = new URLSearchParams();
  if (filters.organization_id) qs.set('organization_id', filters.organization_id);
  if (filters.agent_id) qs.set('agent_id', filters.agent_id);
  if (filters.capability_id) qs.set('capability_id', filters.capability_id);
  // Repeated keys for list-valued query params; FastAPI parses these into a list.
  for (const s of filters.status ?? []) qs.append('status', s);
  for (const s of filters.source ?? []) qs.append('source', s);
  if (filters.created_after) qs.set('created_after', filters.created_after);
  if (filters.created_before) qs.set('created_before', filters.created_before);
  if (filters.success) qs.set('success', filters.success);
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
  if (filters.offset !== undefined) qs.set('offset', String(filters.offset));
  if (filters.parent_task_id) qs.set('parent_task_id', filters.parent_task_id);
  if (filters.top_level) qs.set('top_level', 'true');
  const suffix = qs.toString();
  return `/api/v1/tasks${suffix ? `?${suffix}` : ''}`;
}

export function listTasks(filters: ListTasksFilters = {}): Promise<Task[]> {
  return apiGet<Task[]>(listQuery(filters));
}

/**
 * The same list with its total, for a pager. A server that sends no
 * total (one older than the header) yields the page length, so the
 * page still renders and the pager simply stops at it.
 */
export async function listTasksPage(filters: ListTasksFilters = {}): Promise<TaskPage> {
  const { body, headers } = await apiGetWithHeaders<Task[]>(listQuery(filters));
  const total = Number.parseInt(headers.get(TOTAL_COUNT_HEADER) ?? '', 10);
  return { items: body, total: Number.isNaN(total) ? body.length : total };
}

export function submitTask(body: SubmitTaskBody): Promise<Task> {
  return apiPost<Task>('/api/v1/tasks', body);
}

export function getTask(id: string): Promise<Task> {
  return apiGet<Task>(`/api/v1/tasks/${id}`);
}

export function getTaskInput(id: string): Promise<TaskPayload> {
  return apiGet<TaskPayload>(`/api/v1/tasks/${id}/input`);
}

export function getTaskOutput(id: string): Promise<TaskPayload> {
  return apiGet<TaskPayload>(`/api/v1/tasks/${id}/output`);
}

/** A first task's linkage runs; asked of a linked task, its first task's. */
export function getTaskLinkage(id: string): Promise<TaskLinkage> {
  return apiGet<TaskLinkage>(`/api/v1/tasks/${id}/linkage`);
}

/**
 * The profile of a profiled run.
 *
 * 404 until the task reaches a terminal state, and 404 for anyone who is
 * not an internal principal — the endpoint never confirms a run it will
 * not show.
 */
export function getTaskProfile(id: string): Promise<TaskProfile> {
  return apiGet<TaskProfile>(`/api/v1/tasks/${id}/profile`);
}

/**
 * What the plugin said while the task ran.
 *
 * Internal principals only; anyone else gets 404, as for the profile. A task
 * that exists and has not logged answers the empty shape rather than 404, so
 * a page polling a run in flight reads one shape throughout.
 */
export function getTaskLogs(id: string): Promise<TaskLog> {
  return apiGet<TaskLog>(`/api/v1/tasks/${id}/logs`);
}

/** One observation of a task, as the events endpoint reports it. */
export interface TaskStreamFrame {
  task_id: string;
  status: TaskStatus;
  result: Record<string, unknown> | null;
}

/**
 * Follow a task until it finishes, reporting each result as it arrives.
 *
 * Accepts any task: one that does not stream reports its status changes and
 * ends. `result` carries the whole result each time, never a change to it,
 * so the newest frame is the only one worth keeping.
 *
 * Resolves when the server closes the stream. Abort through `signal` to
 * stop early.
 */
export function streamTask(
  id: string,
  onFrame: (frame: TaskStreamFrame) => void,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  return apiEventStream(
    `/api/v1/tasks/${id}/stream`,
    (event) => {
      if (event.event !== 'snapshot') return;
      try {
        onFrame(JSON.parse(event.data) as TaskStreamFrame);
      } catch {
        // A frame we cannot read is not worth ending the stream over.
      }
    },
    options,
  );
}
